import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { back } from '@/state/nav';
import { profile, recentLogs, animals } from '@/state/data';
import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db';
import { themeMode, setThemeMode, getLocation, setLocation } from '@/lib/theme';
import { ttsMuted, setTtsMuted, listVoices, setTtsVoice, speakText } from '@/lib/tts';
import { continuousMode, setContinuousMode } from '@/state/voice';
import { subscribePush, pushState } from '@/lib/push';
import { showToast, dialog } from '@/state/ui';
import { Icon } from '@/components/Icon';

function Row({ label, sub, children }: { label: string; sub?: string; children?: ComponentChildren }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', padding: '12px 14px', borderBottom: '1px solid var(--stroke-light)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{label}</div>
        {sub && <div class="t-meta">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <span
      class={`switch ${on ? 'is-on' : ''}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      tabIndex={0}
      onClick={() => onChange(!on)}
      onKeyDown={(e) => e.key === 'Enter' && onChange(!on)}
    />
  );
}

export function SettingsScreen() {
  const p = profile.value;
  const [name, setName] = useState(p?.display_name ?? '');
  const [stats, setStats] = useState<{ feeds: number; weights: number } | null>(null);

  useEffect(() => {
    void db.feed_logs.count().then(async (feeds) => {
      const weights = await db.feed_logs.filter((l) => l.weight_g != null).count();
      setStats({ feeds, weights });
    });
  }, []);

  const saveName = async () => {
    const v = name.trim();
    if (v === (p?.display_name ?? '')) return;
    await supabase.from('profile').update({ display_name: v || null }).eq('onerow', true);
    if (profile.value) profile.value = { ...profile.value, display_name: v || null };
    showToast({ kind: 'success', message: 'Saved' });
  };

  const useMyLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setLocation(pos.coords.latitude, pos.coords.longitude);
        await supabase.from('profile').update({ lat: pos.coords.latitude, lng: pos.coords.longitude }).eq('onerow', true);
        showToast({ kind: 'success', message: 'Sunset switching is set for your location' });
      },
      () => showToast({ kind: 'info', message: "Couldn't get location — check permissions" }),
      { timeout: 8000 }
    );
  };

  const enablePush = async () => {
    const r = await subscribePush();
    showToast({
      kind: r === 'subscribed' ? 'success' : 'info',
      message:
        r === 'subscribed'
          ? 'Feeding alarms will arrive as notifications'
          : r === 'denied'
            ? 'Notifications are blocked — allow them in iOS Settings → Tend'
            : r === 'unsupported'
              ? 'Install Tend to the Home Screen first (Share → Add to Home Screen)'
              : 'Push setup failed — try again on the installed app',
    }, 5000);
  };

  const voices = listVoices();

  const resetLocal = () => {
    dialog.value = {
      title: 'Reset this device?',
      message: 'Clears the local cache and signs out. Your animals and history stay safe on the server.',
      confirmLabel: 'Reset',
      danger: true,
      onConfirm: async () => {
        await db.delete();
        await supabase.auth.signOut();
        location.href = '/';
      },
    };
  };

  const activeCount = [...animals.value.values()].filter((a) => a.status === 'active').length;
  const releasedCount = [...animals.value.values()].filter((a) => a.status === 'released').length;

  return (
    <div class="screen screen--no-tabbar screen--fade-in">
      <header class="header header--sub">
        <button class="iconbtn" aria-label="Back" onClick={() => back('/more')}>
          <Icon name="chevron-left" size={20} />
        </button>
        <div>
          <div class="header__title">Settings</div>
        </div>
      </header>

      <div class="scroll" style={{ gap: '18px' }}>
        <div>
          <p class="t-eyebrow" style={{ margin: '8px 4px' }}>You</p>
          <div class="card">
            <Row label="Your name" sub="Used in greetings and reports">
              <input
                class="input"
                style={{ width: '130px', minHeight: '40px', padding: '6px 10px' }}
                value={name}
                aria-label="Your name"
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                onBlur={saveName}
              />
            </Row>
          </div>
        </div>

        <div>
          <p class="t-eyebrow" style={{ margin: '0 4px 8px' }}>Theme</p>
          <div class="card">
            <Row label="Appearance" sub={themeMode.value === 'auto' ? 'Follows the sun' : themeMode.value}>
              <div class="seg-grid seg-grid--3" style={{ width: '190px' }}>
                {(['auto', 'light', 'dark'] as const).map((m) => (
                  <button key={m} class={`seg ${themeMode.value === m ? 'is-on' : ''}`} style={{ height: '34px', fontSize: '11px' }} onClick={() => setThemeMode(m)}>
                    {m === 'auto' ? 'Sunset' : m[0].toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </Row>
            <Row label="Location for sunset" sub={getLocation() ? 'Set — dark mode lands at dusk' : 'Not set — using system setting'}>
              <button class="btn btn--secondary btn--sm" onClick={useMyLocation}>
                {getLocation() ? 'Update' : 'Use mine'}
              </button>
            </Row>
          </div>
        </div>

        <div>
          <p class="t-eyebrow" style={{ margin: '0 4px 8px' }}>Notifications</p>
          <div class="card">
            <Row
              label="Feeding alarms"
              sub={pushState.value === 'subscribed' ? 'On — pushes when feedings come due' : 'Get pushed when feedings come due'}
            >
              {pushState.value === 'subscribed' ? (
                <span class="pill pill--accent">On</span>
              ) : (
                <button class="btn btn--sm" onClick={enablePush}>Enable</button>
              )}
            </Row>
          </div>
        </div>

        <div>
          <p class="t-eyebrow" style={{ margin: '0 4px 8px' }}>Voice</p>
          <div class="card">
            <Row label="Speak replies aloud" sub="Tend reads answers back after voice questions">
              <Toggle on={!ttsMuted.value} onChange={(v) => setTtsMuted(!v)} label="Speak replies" />
            </Row>
            <Row label="Continuous conversation" sub="Keeps listening ~5s after Tend finishes speaking">
              <Toggle on={continuousMode.value} onChange={setContinuousMode} label="Continuous conversation" />
            </Row>
            {voices.length > 1 && (
              <Row label="Voice" sub="Which voice reads aloud">
                <select
                  class="select"
                  style={{ width: '150px', minHeight: '40px', padding: '6px 10px' }}
                  aria-label="TTS voice"
                  onChange={(e) => {
                    setTtsVoice((e.target as HTMLSelectElement).value);
                    speakText('Hi — this is how I sound.');
                  }}
                >
                  {voices.map((v) => (
                    <option key={v.name} value={v.name} selected={localStorage.getItem('tend.tts.voice') === v.name}>
                      {v.name.replace(/Microsoft |Google /, '').slice(0, 24)}
                    </option>
                  ))}
                </select>
              </Row>
            )}
          </div>
        </div>

        <div>
          <p class="t-eyebrow" style={{ margin: '0 4px 8px' }}>Care stats</p>
          <div class="card card__body" style={{ display: 'flex', gap: 'var(--s-5)', padding: '14px' }}>
            <div>
              <div class="t-h2" style={{ margin: 0 }}>{activeCount}</div>
              <div class="t-meta">in care</div>
            </div>
            <div>
              <div class="t-h2" style={{ margin: 0 }}>{releasedCount}</div>
              <div class="t-meta">released</div>
            </div>
            <div>
              <div class="t-h2" style={{ margin: 0 }}>{stats?.feeds ?? '–'}</div>
              <div class="t-meta">feeds logged</div>
            </div>
            <div>
              <div class="t-h2" style={{ margin: 0 }}>{stats?.weights ?? '–'}</div>
              <div class="t-meta">weights</div>
            </div>
          </div>
        </div>

        <div>
          <p class="t-eyebrow" style={{ margin: '0 4px 8px' }}>Device</p>
          <div class="card">
            <Row label="Synced" sub={`${recentLogs.value.length} recent entries cached for offline`} />
            <Row label="Reset this device" sub="Clear cache & sign out (server data stays)">
              <button class="btn btn--danger btn--sm" onClick={resetLocal}>Reset</button>
            </Row>
          </div>
        </div>

        <p class="t-meta" style={{ textAlign: 'center', margin: '4px 0 12px' }}>
          Tend · Stark Parks Wildlife Conservation Center
        </p>
      </div>
    </div>
  );
}
